import { HttpErrorResponse } from '@angular/common/http';

import type {
  InterviewTemplateDetail,
  InterviewTemplateKind,
} from '@recruitee/interview-templates-types';
import {
  CorrelationId,
  createEntityCollectionStore,
  getEntityCollectionActions,
  pipeState,
} from '@recruitee/ngrx';

const idSelector = ({ id }: { id: number }) => id;
const { actions, initialState, namespace, reducer, selectors } =
  createEntityCollectionStore<InterviewTemplateDetail>()('InterviewTemplatesDetails', {
    idSelector,
    customActions: adapter => {
      const reducerFunctions = getEntityCollectionActions(adapter, idSelector);

      return {
        updateOnePessimistic: (
          state,
          payload: {
            data: { id: number; category: string; name: string; kind: InterviewTemplateKind };
          },
        ) => reducerFunctions.updateOnePessimistic(state, payload),

        duplicate: (state, payload: { id: number } & CorrelationId) => state,
        duplicateSuccess: (
          state,
          payload: { sourceId: number; data: InterviewTemplateDetail } & CorrelationId,
        ) =>
          pipeState(
            state,
            next => adapter.writeOne(next, payload.data),
            next => adapter.markAsLoaded(next, { entities: [payload.data.id], scope: null }),
          ),
        duplicateFailed: (
          state,
          { error, correlationId }: { sourceId: number; error: HttpErrorResponse } & CorrelationId,
        ) =>
          adapter.setError(state, error, {
            entities: correlationId ? [correlationId] : [],
            scope: null,
          }),
      };
    },
  });

const InterviewTemplateDetailsActions = {
  fetchOne: actions.fetchOne,
  fetchOneSuccess: actions.fetchOneSuccess,
  fetchOneFailed: actions.fetchOneFailed,

  createOnePessimistic: actions.createOnePessimistic,
  createOnePessimisticFailed: actions.createOnePessimisticFailed,
  createOnePessimisticSuccess: actions.createOnePessimisticSuccess,

  removeOnePessimistic: actions.removeOnePessimistic,
  removeOnePessimisticFailed: actions.removeOnePessimisticFailed,
  removeOnePessimisticSuccess: actions.removeOnePessimisticSuccess,

  updateOnePessimistic: actions.updateOnePessimistic,
  updateOnePessimisticFailed: actions.updateOnePessimisticFailed,
  updateOnePessimisticSuccess: actions.updateOnePessimisticSuccess,

  duplicate: actions.duplicate,
  duplicateSuccess: actions.duplicateSuccess,
  duplicateFailed: actions.duplicateFailed,
};

export {
  actions,
  namespace as InterviewTemplateDetailsNamespace,
  initialState as InterviewTemplateDetailsInitialState,
  reducer as InterviewTemplateDetailsReducer,
  InterviewTemplateDetailsActions,
  selectors as InterviewTemplateDetailsSelectors,
};
